import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { 
    fetchAllJobs,
    updateJobStatus,
    deleteJob,
    bulkDeleteJobs,
    selectAllJobs,
    selectTotalJobs,
    selectLoading,
    selectError,
    clearError,
    resetAdminState,
    checkSessionStorageAuth,
} from '../_store/masterAuthSlice';
import { masterLogout, selectIsAuthenticated } from '../_store/masterAuthSlice';
import UAVSphere_logo from "../assets/images/UAVspherelogo.png";

export const SuperAdmin = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    
    const isAuthenticated = useSelector(selectIsAuthenticated);
    const jobs = useSelector(selectAllJobs);
    const totalJobs = useSelector(selectTotalJobs);
    const loading = useSelector(selectLoading);
    const error = useSelector(selectError);
    
    // UI State
    const [statusFilter, setStatusFilter] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedJob, setSelectedJob] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [jobToDelete, setJobToDelete] = useState(null);
    const [editingDescription, setEditingDescription] = useState(false);
    const [enhancedDescription, setEnhancedDescription] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [showSuccessToast, setShowSuccessToast] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [selectedJobs, setSelectedJobs] = useState([]);
    const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [editingJob, setEditingJob] = useState(null);
    const [editFormData, setEditFormData] = useState({ name: '', location: '' });
    
    const jobsPerPage = 20;
    

    const handleViewUsersList = () => {
    navigate('/UsersList');
};

    // Check authentication on mount
    useEffect(() => {
        dispatch(checkSessionStorageAuth());
    }, [dispatch]);
    
    // Fetch jobs when authenticated or filters change
    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/admin/login');
            return;
        }
        
        fetchJobs();
        
        return () => {
            dispatch(resetAdminState());
        };
    }, [dispatch, isAuthenticated, navigate, statusFilter]);
    
    const fetchJobs = useCallback(async () => {
        try {
            await dispatch(fetchAllJobs({ 
                status: statusFilter || undefined,
                limit: 1000
            })).unwrap();
        } catch (error) {
            if (error.message === 'No session found') {
                navigate('/admin/login');
            }
        }
    }, [dispatch, statusFilter, navigate]);
    
    const handleRefresh = async () => {
        setIsRefreshing(true);
        await fetchJobs();
        setTimeout(() => setIsRefreshing(false), 500);
        showSuccessToastMessage('Jobs refreshed successfully');
    };
    
    const showSuccessToastMessage = (message) => {
        setSuccessMessage(message);
        setShowSuccessToast(true);
        setTimeout(() => setShowSuccessToast(false), 3000);
    };
    
    const handleStatusFilterChange = (status) => {
        setStatusFilter(status);
        setCurrentPage(1);
        setSelectedJobs([]);
    };
    
    const handleStatusUpdate = async (jobId, newStatus) => {
        try {
            const result = await dispatch(updateJobStatus({ 
                jobId, 
                updates: { 
                    status: newStatus,
                    isApproved: newStatus === 'approved'
                } 
            })).unwrap();
            
            if (selectedJob && selectedJob._id === jobId) {
                setSelectedJob({
                    ...selectedJob,
                    status: newStatus,
                    isApproved: newStatus === 'approved'
                });
            }
            
            showSuccessToastMessage(`Job ${newStatus === 'approved' ? 'approved' : 'rejected'} successfully!`);
        } catch (error) {
            console.error('Failed to update status:', error);
            if (error.message === 'No session found') {
                navigate('/admin/login');
            }
            showSuccessToastMessage('Failed to update status. Please try again.');
        }
    };
    
    const handleDeleteJob = async () => {
        if (!jobToDelete) return;
        
        try {
            await dispatch(deleteJob(jobToDelete._id)).unwrap();
            setShowDeleteConfirm(false);
            setJobToDelete(null);
            
            if (selectedJob && selectedJob._id === jobToDelete._id) {
                setShowModal(false);
                setSelectedJob(null);
            }
            
            showSuccessToastMessage('Job deleted successfully!');
        } catch (error) {
            console.error('Failed to delete job:', error);
            if (error.message === 'No session found') {
                navigate('/admin/login');
            }
            showSuccessToastMessage('Failed to delete job. Please try again.');
        }
    };
    
    const handleBulkDelete = async () => {
        if (selectedJobs.length === 0) return;
        
        try {
            await dispatch(bulkDeleteJobs(selectedJobs)).unwrap();
            setSelectedJobs([]);
            setShowBulkDeleteConfirm(false);
            showSuccessToastMessage(`${selectedJobs.length} job(s) deleted successfully!`);
        } catch (error) {
            console.error('Failed to delete jobs:', error);
            showSuccessToastMessage('Failed to delete some jobs. Please try again.');
        }
    };
    
    const handleUpdateDescription = async () => {
        if (!selectedJob) return;
        
        try {
            await dispatch(updateJobStatus({ 
                jobId: selectedJob._id, 
                updates: { enhancedDescription } 
            })).unwrap();
            
            setSelectedJob({
                ...selectedJob,
                enhancedDescription
            });
            setEditingDescription(false);
            showSuccessToastMessage('Description updated successfully!');
        } catch (error) {
            console.error('Failed to update description:', error);
            if (error.message === 'No session found') {
                navigate('/admin/login');
            }
            showSuccessToastMessage('Failed to update description. Please try again.');
        }
    };
    
    const handleEditJob = (job) => {
        setEditingJob(job);
        setEditFormData({
            name: job.name || '',
            location: job.location || ''
        });
    };
    
    const handleSaveEdit = async () => {
        if (!editingJob) return;
        
        try {
            await dispatch(updateJobStatus({ 
                jobId: editingJob._id, 
                updates: { 
                    name: editFormData.name,
                    location: editFormData.location
                } 
            })).unwrap();
            
            setEditingJob(null);
            showSuccessToastMessage('Job details updated successfully!');
            await fetchJobs(); // Refresh the list
        } catch (error) {
            console.error('Failed to update job:', error);
            showSuccessToastMessage('Failed to update job details. Please try again.');
        }
    };
    
    const handleViewDetails = (job) => {
        setSelectedJob(job);
        setEnhancedDescription(job.enhancedDescription || job.originalDescription || '');
        setShowModal(true);
        setEditingDescription(false);
    };
    
    const handleSelectJob = (jobId) => {
        setSelectedJobs(prev => 
            prev.includes(jobId) 
                ? prev.filter(id => id !== jobId)
                : [...prev, jobId]
        );
    };
    
    const handleSelectAll = () => {
        if (selectedJobs.length === currentJobs.length) {
            setSelectedJobs([]);
        } else {
            setSelectedJobs(currentJobs.map(job => job._id));
        }
    };
    
    const handleLogout = async () => {
        await dispatch(masterLogout());
        navigate('/admin/login');
    };
    
    // Filter jobs based on search
    const filteredJobs = jobs.filter(job => {
        const searchLower = searchTerm.toLowerCase();
        return (
            job.name?.toLowerCase().includes(searchLower) ||
            job.email?.toLowerCase().includes(searchLower) ||
            job.location?.toLowerCase().includes(searchLower) ||
            job.originalDescription?.toLowerCase().includes(searchLower) ||
            job.enhancedDescription?.toLowerCase().includes(searchLower)
        );
    });
    
    // Pagination
    const indexOfLastJob = currentPage * jobsPerPage;
    const indexOfFirstJob = indexOfLastJob - jobsPerPage;
    const currentJobs = filteredJobs.slice(indexOfFirstJob, indexOfLastJob);
    const totalPages = Math.ceil(filteredJobs.length / jobsPerPage);
    
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };
    
    const getStatusBadge = (status) => {
        const badges = {
            pending: { background: '#ffc107', color: '#000', text: 'Pending', icon: '⏳' },
            approved: { background: '#28a745', color: 'white', text: 'Approved', icon: '✓' },
            rejected: { background: '#dc3545', color: 'white', text: 'Rejected', icon: '✗' }
        };
        
        const badge = badges[status] || badges.pending;
        
        return (
            <span style={{
                ...styles.statusBadge,
                background: badge.background,
                color: badge.color
            }}>
                {badge.icon} {badge.text}
            </span>
        );
    };
    
    const getStatusStats = () => {
        const stats = {
            total: jobs.length,
            pending: jobs.filter(j => j.status === 'pending').length,
            approved: jobs.filter(j => j.status === 'approved').length,
            rejected: jobs.filter(j => j.status === 'rejected').length
        };
        return stats;
    };
    
    const stats = getStatusStats();
    
    const styles = {
        container: {
            minHeight: '100vh',
            background: '#f5f5f5',
            padding: '1rem'
        },
        toast: {
            position: 'fixed',
            top: '20px',
            right: '20px',
            background: '#28a745',
            color: 'white',
            padding: '1rem 1.5rem',
            borderRadius: '8px',
            zIndex: 2000,
            animation: 'slideIn 0.3s ease-out',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        },
        header: {
            background: 'white',
            padding: '1.5rem 2rem',
            borderRadius: '12px',
            marginBottom: '1.5rem',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '1rem'
        },
        headerLeft: {
            display: 'flex',
            alignItems: 'center',
            gap: '1.5rem',
            flexWrap: 'wrap'
        },
        logo: {
            width: '120px',
            height: 'auto'
        },
        title: {
            fontSize: '1.75rem',
            fontWeight: '700',
            color: '#333',
            margin: 0
        },
        subtitle: {
            color: '#666',
            fontSize: '0.9rem',
            marginTop: '0.25rem'
        },
        headerRight: {
            display: 'flex',
            gap: '1rem',
            alignItems: 'center',
            flexWrap: 'wrap'
        },
        statsContainer: {
            display: 'flex',
            gap: '1rem'
        },
        statsCard: {
            background: 'white',
            padding: '0.75rem 1.25rem',
            borderRadius: '8px',
            textAlign: 'center',
            border: '1px solid #e1e5e9',
            minWidth: '80px',
            cursor: 'pointer',
            transition: 'transform 0.2s'
        },
        statsNumber: {
            fontSize: '1.5rem',
            fontWeight: '700',
            color: '#2a5298'
        },
        statsLabel: {
            fontSize: '0.75rem',
            color: '#666',
            marginTop: '0.25rem'
        },
        logoutButton: {
            padding: '0.6rem 1.5rem',
            background: '#dc3545',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '0.9rem',
            fontWeight: '500',
            transition: 'all 0.2s'
        },
        controlsContainer: {
            background: 'white',
            padding: '1.5rem',
            borderRadius: '12px',
            marginBottom: '1.5rem',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        },
        filterGroup: {
            display: 'flex',
            gap: '1rem',
            marginBottom: '1rem',
            flexWrap: 'wrap'
        },
        filterButton: {
            padding: '0.6rem 1.5rem',
            background: 'white',
            border: '2px solid #e1e5e9',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '0.9rem',
            fontWeight: '500',
            transition: 'all 0.2s'
        },
        filterButtonActive: {
            background: '#2a5298',
            color: 'white',
            borderColor: '#2a5298'
        },
        searchContainer: {
            display: 'flex',
            gap: '1rem',
            flexWrap: 'wrap'
        },
        searchInput: {
            flex: 1,
            minWidth: '300px',
            padding: '0.75rem 1rem',
            border: '2px solid #e1e5e9',
            borderRadius: '8px',
            fontSize: '0.95rem',
            transition: 'border-color 0.2s'
        },
        refreshButton: {
            padding: '0.75rem 1.5rem',
            background: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '0.9rem',
            transition: 'all 0.2s',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
        },
        bulkActions: {
            display: 'flex',
            gap: '1rem',
            marginTop: '1rem',
            padding: '1rem',
            background: '#f8f9fa',
            borderRadius: '8px',
            alignItems: 'center'
        },
        bulkDeleteButton: {
            padding: '0.5rem 1rem',
            background: '#dc3545',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '0.85rem'
        },
        tableContainer: {
            background: 'white',
            borderRadius: '12px',
            overflow: 'auto',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            marginBottom: '1.5rem'
        },
        table: {
            width: '100%',
            borderCollapse: 'collapse',
            minWidth: '800px'
        },
        th: {
            textAlign: 'left',
            padding: '1rem',
            background: '#f8f9fa',
            borderBottom: '2px solid #dee2e6',
            fontWeight: '600',
            color: '#555',
            fontSize: '0.9rem'
        },
        td: {
            padding: '1rem',
            borderBottom: '1px solid #eee',
            fontSize: '0.9rem'
        },
        checkbox: {
            width: '20px',
            height: '20px',
            cursor: 'pointer'
        },
        descriptionPreview: {
            maxWidth: '300px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            color: '#666'
        },
        actionButton: {
            padding: '0.4rem 0.8rem',
            margin: '0 0.25rem',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.8rem',
            fontWeight: '500',
            transition: 'all 0.2s'
        },
        statusBadge: {
            display: 'inline-block',
            padding: '0.3rem 0.8rem',
            borderRadius: '20px',
            fontSize: '0.8rem',
            fontWeight: '600'
        },
        pagination: {
            display: 'flex',
            justifyContent: 'center',
            gap: '0.5rem',
            marginTop: '1.5rem',
            flexWrap: 'wrap'
        },
        pageButton: {
            padding: '0.5rem 1rem',
            border: '1px solid #ddd',
            background: 'white',
            borderRadius: '4px',
            cursor: 'pointer',
            transition: 'all 0.2s'
        },
        activePageButton: {
            padding: '0.5rem 1rem',
            border: '1px solid #2a5298',
            background: '#2a5298',
            color: 'white',
            borderRadius: '4px',
            cursor: 'pointer'
        },
        modalOverlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
            padding: '1rem'
        },
        modalContent: {
            background: 'white',
            borderRadius: '12px',
            maxWidth: '900px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
        },
        modalHeader: {
            padding: '1.5rem',
            borderBottom: '1px solid #eee',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            position: 'sticky',
            top: 0,
            background: 'white'
        },
        modalTitle: {
            fontSize: '1.5rem',
            fontWeight: '600',
            color: '#333',
            margin: 0
        },
        closeButton: {
            background: 'none',
            border: 'none',
            fontSize: '1.5rem',
            cursor: 'pointer',
            color: '#999',
            padding: '0.5rem'
        },
        modalBody: {
            padding: '1.5rem'
        },
        modalSection: {
            marginBottom: '1.5rem',
            padding: '1rem',
            background: '#f8f9fa',
            borderRadius: '8px'
        },
        modalLabel: {
            fontWeight: '600',
            color: '#555',
            marginBottom: '0.5rem',
            fontSize: '0.9rem',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
        },
        modalValue: {
            color: '#333',
            lineHeight: '1.6'
        },
        textarea: {
            width: '100%',
            minHeight: '200px',
            padding: '1rem',
            border: '1px solid #ddd',
            borderRadius: '8px',
            fontSize: '0.95rem',
            lineHeight: '1.6',
            marginBottom: '1rem',
            fontFamily: 'inherit'
        },
        modalFooter: {
            padding: '1.5rem',
            borderTop: '1px solid #eee',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '1rem',
            position: 'sticky',
            bottom: 0,
            background: 'white'
        },
        loadingContainer: {
            textAlign: 'center',
            padding: '3rem',
            color: '#666'
        },
        errorContainer: {
            background: '#f8d7da',
            color: '#721c24',
            padding: '1rem',
            borderRadius: '8px',
            marginBottom: '1rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
        },
        editInput: {
            padding: '0.4rem',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '0.9rem',
            width: '100%'
        }
    };
    
    // Add keyframes for animations
    const styleSheet = document.createElement("style");
    styleSheet.textContent = `
        @keyframes slideIn {
            from {
                transform: translateX(100%);
                opacity: 0;
            }
            to {
                transform: translateX(0);
                opacity: 1;
            }
        }
        
        @keyframes spin {
            from {
                transform: rotate(0deg);
            }
            to {
                transform: rotate(360deg);
            }
        }
    `;
    document.head.appendChild(styleSheet);
    
    if (!isAuthenticated) {
        return null;
    }
    
    return (
        <div style={styles.container}>
            {/* Success Toast */}
            {showSuccessToast && (
                <div style={styles.toast}>
                    ✓ {successMessage}
                </div>
            )}
            
            {/* Header */}
            <div style={styles.header}>
                <div style={styles.headerLeft}>
                    <img src={UAVSphere_logo} alt="UAVSphere" style={styles.logo} />
                    <div>
                        <h1 style={styles.title}>Super Admin Dashboard</h1>
                        <p style={styles.subtitle}>Complete control over job postings</p>
                    </div>
                </div>
                  <button 
        style={{
            ...styles.logoutButton, 
            background: '#28a745',
            marginRight: '10px'
        }} 
        onClick={handleViewUsersList}
    >
        👥 See All Providers List
    </button>
                <div style={styles.headerRight}>
                    <div style={styles.statsContainer}>
                        <div style={styles.statsCard} onClick={() => handleStatusFilterChange('')}>
                            <div style={styles.statsNumber}>{stats.total}</div>
                            <div style={styles.statsLabel}>Total</div>
                        </div>
                        <div style={styles.statsCard} onClick={() => handleStatusFilterChange('pending')}>
                            <div style={styles.statsNumber}>{stats.pending}</div>
                            <div style={styles.statsLabel}>Pending</div>
                        </div>
                        <div style={styles.statsCard} onClick={() => handleStatusFilterChange('approved')}>
                            <div style={styles.statsNumber}>{stats.approved}</div>
                            <div style={styles.statsLabel}>Approved</div>
                        </div>
                        <div style={styles.statsCard} onClick={() => handleStatusFilterChange('rejected')}>
                            <div style={styles.statsNumber}>{stats.rejected}</div>
                            <div style={styles.statsLabel}>Rejected</div>
                        </div>
                    </div>
                    <button style={styles.logoutButton} onClick={handleLogout}>
                        Logout
                    </button>
                </div>
            </div>
            
            {/* Controls */}
            <div style={styles.controlsContainer}>
                <div style={styles.filterGroup}>
                    <button 
                        style={{
                            ...styles.filterButton,
                            ...(statusFilter === '' ? styles.filterButtonActive : {})
                        }}
                        onClick={() => handleStatusFilterChange('')}
                    >
                        All Jobs
                    </button>
                    <button 
                        style={{
                            ...styles.filterButton,
                            ...(statusFilter === 'pending' ? styles.filterButtonActive : {})
                        }}
                        onClick={() => handleStatusFilterChange('pending')}
                    >
                        Pending
                    </button>
                    <button 
                        style={{
                            ...styles.filterButton,
                            ...(statusFilter === 'approved' ? styles.filterButtonActive : {})
                        }}
                        onClick={() => handleStatusFilterChange('approved')}
                    >
                        Approved
                    </button>
                    <button 
                        style={{
                            ...styles.filterButton,
                            ...(statusFilter === 'rejected' ? styles.filterButtonActive : {})
                        }}
                        onClick={() => handleStatusFilterChange('rejected')}
                    >
                        Rejected
                    </button>
                </div>
                <div style={styles.searchContainer}>
                    <input
                        type="text"
                        placeholder="Search by title, email, location, or description..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={styles.searchInput}
                    />
                    <button 
                        style={styles.refreshButton}
                        onClick={handleRefresh}
                        disabled={isRefreshing}
                    >
                        {isRefreshing ? '⟳' : '🔄'} Refresh
                    </button>
                </div>
                
                {/* Bulk Actions */}
                {selectedJobs.length > 0 && (
                    <div style={styles.bulkActions}>
                        <span>{selectedJobs.length} job(s) selected</span>
                        <button 
                            style={styles.bulkDeleteButton}
                            onClick={() => setShowBulkDeleteConfirm(true)}
                        >
                            Delete Selected
                        </button>
                        <button 
                            style={{...styles.bulkDeleteButton, background: '#6c757d'}}
                            onClick={() => setSelectedJobs([])}
                        >
                            Clear Selection
                        </button>
                    </div>
                )}
            </div>
            
            {/* Error Message */}
            {error && (
                <div style={styles.errorContainer}>
                    ⚠️ {error}
                    <button 
                        onClick={() => dispatch(clearError())}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem' }}
                    >
                        ✕
                    </button>
                </div>
            )}
            
            {/* Jobs Table */}
            <div style={styles.tableContainer}>
                {loading ? (
                    <div style={styles.loadingContainer}>
                        <div>Loading jobs...</div>
                    </div>
                ) : (
                    <>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>
                                        <input
                                            type="checkbox"
                                            style={styles.checkbox}
                                            checked={selectedJobs.length === currentJobs.length && currentJobs.length > 0}
                                            onChange={handleSelectAll}
                                        />
                                    </th>
                                    <th style={styles.th}>Date</th>
                                    <th style={styles.th}>Title</th>
                                    <th style={styles.th}>Email</th>
                                    <th style={styles.th}>Location</th>
                                    <th style={styles.th}>Description</th>
                                    <th style={styles.th}>Status</th>
                                    <th style={styles.th}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentJobs.map(job => (
                                    <tr key={job._id} style={{ backgroundColor: selectedJobs.includes(job._id) ? '#f0f8ff' : 'transparent' }}>
                                        <td style={styles.td}>
                                            <input
                                                type="checkbox"
                                                style={styles.checkbox}
                                                checked={selectedJobs.includes(job._id)}
                                                onChange={() => handleSelectJob(job._id)}
                                            />
                                        </td>
                                        <td style={styles.td}>{formatDate(job.createdAt)}</td>
                                        <td style={styles.td}>
                                            {editingJob && editingJob._id === job._id ? (
                                                <input
                                                    type="text"
                                                    value={editFormData.name}
                                                    onChange={(e) => setEditFormData({...editFormData, name: e.target.value})}
                                                    style={styles.editInput}
                                                />
                                            ) : (
                                                <strong>{job.name || 'N/A'}</strong>
                                            )}
                                        </td>
                                        <td style={styles.td}>{job.email || 'N/A'}</td>
                                        <td style={styles.td}>
                                            {editingJob && editingJob._id === job._id ? (
                                                <input
                                                    type="text"
                                                    value={editFormData.location}
                                                    onChange={(e) => setEditFormData({...editFormData, location: e.target.value})}
                                                    style={styles.editInput}
                                                />
                                            ) : (
                                                job.location || 'N/A'
                                            )}
                                        </td>
                                        <td style={styles.td}>
                                            <div style={styles.descriptionPreview} title={job.enhancedDescription || job.originalDescription}>
                                                {job.enhancedDescription || job.originalDescription || 'No description'}
                                            </div>
                                        </td>
                                        <td style={styles.td}>{getStatusBadge(job.status)}</td>
                                        <td style={styles.td}>
                                            {editingJob && editingJob._id === job._id ? (
                                                <>
                                                    <button
                                                        style={{...styles.actionButton, background: '#28a745', color: 'white'}}
                                                        onClick={handleSaveEdit}
                                                    >
                                                        Save
                                                    </button>
                                                    <button
                                                        style={{...styles.actionButton, background: '#6c757d', color: 'white'}}
                                                        onClick={() => setEditingJob(null)}
                                                    >
                                                        Cancel
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    <button
                                                        style={{...styles.actionButton, background: '#007bff', color: 'white'}}
                                                        onClick={() => handleViewDetails(job)}
                                                        title="View Details"
                                                    >
                                                        👁️ View
                                                    </button>
                                                    <button
                                                        style={{...styles.actionButton, background: '#ffc107', color: '#000'}}
                                                        onClick={() => handleEditJob(job)}
                                                        title="Edit Job"
                                                    >
                                                        ✏️ Edit
                                                    </button>
                                                    {job.status !== 'approved' && (
                                                        <button
                                                            style={{...styles.actionButton, background: '#28a745', color: 'white'}}
                                                            onClick={() => handleStatusUpdate(job._id, 'approved')}
                                                            title="Approve Job"
                                                        >
                                                            ✓ Approve
                                                        </button>
                                                    )}
                                                    {job.status !== 'rejected' && (
                                                        <button
                                                            style={{...styles.actionButton, background: '#ff9800', color: 'white'}}
                                                            onClick={() => handleStatusUpdate(job._id, 'rejected')}
                                                            title="Reject Job"
                                                        >
                                                            ✗ Reject
                                                        </button>
                                                    )}
                                                    <button
                                                        style={{...styles.actionButton, background: '#dc3545', color: 'white'}}
                                                        onClick={() => {
                                                            setJobToDelete(job);
                                                            setShowDeleteConfirm(true);
                                                        }}
                                                        title="Delete Job"
                                                    >
                                                        🗑️ Delete
                                                    </button>
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        
                        {filteredJobs.length === 0 && (
                            <div style={styles.loadingContainer}>
                                No jobs found matching your criteria
                            </div>
                        )}
                    </>
                )}
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
                <div style={styles.pagination}>
                    <button
                        style={styles.pageButton}
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                    >
                        ← Previous
                    </button>
                    
                    {[...Array(Math.min(totalPages, 7))].map((_, i) => {
                        let page;
                        if (totalPages <= 7) {
                            page = i + 1;
                        } else if (currentPage <= 4) {
                            page = i + 1;
                            if (i === 6) page = totalPages;
                        } else if (currentPage >= totalPages - 3) {
                            page = totalPages - 6 + i;
                        } else {
                            page = currentPage - 3 + i;
                            if (i === 0) page = 1;
                            if (i === 6) page = totalPages;
                        }
                        
                        if (page === currentPage) {
                            return (
                                <button key={page} style={styles.activePageButton}>
                                    {page}
                                </button>
                            );
                        }
                        
                        if ((i === 0 && page > 1) || (i === 6 && page < totalPages - 1)) {
                            return <span key={`dots-${page}`} style={{ padding: '0.5rem' }}>...</span>;
                        }
                        
                        return (
                            <button
                                key={page}
                                style={styles.pageButton}
                                onClick={() => setCurrentPage(page)}
                            >
                                {page}
                            </button>
                        );
                    })}
                    
                    <button
                        style={styles.pageButton}
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                    >
                        Next →
                    </button>
                </div>
            )}
            
            {/* Job Details Modal */}
            {showModal && selectedJob && (
                <div style={styles.modalOverlay} onClick={() => setShowModal(false)}>
                    <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div style={styles.modalHeader}>
                            <h2 style={styles.modalTitle}>Job Details: {selectedJob.name}</h2>
                            <button style={styles.closeButton} onClick={() => setShowModal(false)}>✕</button>
                        </div>
                        
                        <div style={styles.modalBody}>
                            <div style={styles.modalSection}>
                                <div style={styles.modalLabel}>📋 Job Information</div>
                                <div style={styles.modalValue}>
                                    <p><strong>Email:</strong> {selectedJob.email}</p>
                                    <p><strong>Contact:</strong> {selectedJob.contactNumber || 'N/A'}</p>
                                    <p><strong>Location:</strong> {selectedJob.location}</p>
                                    <p><strong>Status:</strong> {getStatusBadge(selectedJob.status)}</p>
                                    <p><strong>Created:</strong> {formatDate(selectedJob.createdAt)}</p>
                                    <p><strong>Last Updated:</strong> {formatDate(selectedJob.updatedAt)}</p>
                                </div>
                            </div>
                            
                            <div style={styles.modalSection}>
                                <div style={styles.modalLabel}>📝 Original Description</div>
                                <div style={styles.modalValue}>
                                    {selectedJob.originalDescription || 'No original description'}
                                </div>
                            </div>
                            
                            <div style={styles.modalSection}>
                                <div style={styles.modalLabel}>✨ Enhanced Description</div>
                                {editingDescription ? (
                                    <>
                                        <textarea
                                            value={enhancedDescription}
                                            onChange={(e) => setEnhancedDescription(e.target.value)}
                                            style={styles.textarea}
                                            placeholder="Enter enhanced description..."
                                        />
                                        <div>
                                            <button
                                                style={{...styles.actionButton, background: '#28a745', color: 'white', padding: '0.6rem 1.5rem'}}
                                                onClick={handleUpdateDescription}
                                            >
                                                💾 Save Changes
                                            </button>
                                            <button
                                                style={{...styles.actionButton, background: '#6c757d', color: 'white', padding: '0.6rem 1.5rem', marginLeft: '0.5rem'}}
                                                onClick={() => {
                                                    setEnhancedDescription(selectedJob.enhancedDescription || selectedJob.originalDescription || '');
                                                    setEditingDescription(false);
                                                }}
                                            >
                                                ❌ Cancel
                                            </button>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div style={styles.modalValue}>
                                            {selectedJob.enhancedDescription || 'No enhanced description yet'}
                                        </div>
                                        <button
                                            style={{...styles.actionButton, background: '#007bff', color: 'white', marginTop: '0.5rem'}}
                                            onClick={() => setEditingDescription(true)}
                                        >
                                            ✏️ Edit Description
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                        
                        <div style={styles.modalFooter}>
                            <button
                                style={{...styles.actionButton, background: '#6c757d', color: 'white', padding: '0.6rem 1.5rem'}}
                                onClick={() => setShowModal(false)}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && jobToDelete && (
                <div style={styles.modalOverlay} onClick={() => setShowDeleteConfirm(false)}>
                    <div style={{...styles.modalContent, maxWidth: '400px'}} onClick={(e) => e.stopPropagation()}>
                        <div style={styles.modalHeader}>
                            <h3 style={styles.modalTitle}>Confirm Delete</h3>
                        </div>
                        <div style={styles.modalBody}>
                            <p>Are you sure you want to delete this job posting?</p>
                            <p><strong>{jobToDelete.name}</strong></p>
                            <p>This action cannot be undone.</p>
                        </div>
                        <div style={styles.modalFooter}>
                            <button
                                style={{...styles.actionButton, background: '#dc3545', color: 'white', padding: '0.6rem 1.5rem'}}
                                onClick={handleDeleteJob}
                            >
                                Yes, Delete
                            </button>
                            <button
                                style={{...styles.actionButton, background: '#6c757d', color: 'white', padding: '0.6rem 1.5rem'}}
                                onClick={() => setShowDeleteConfirm(false)}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Bulk Delete Confirmation Modal */}
            {showBulkDeleteConfirm && (
                <div style={styles.modalOverlay} onClick={() => setShowBulkDeleteConfirm(false)}>
                    <div style={{...styles.modalContent, maxWidth: '400px'}} onClick={(e) => e.stopPropagation()}>
                        <div style={styles.modalHeader}>
                            <h3 style={styles.modalTitle}>Confirm Bulk Delete</h3>
                        </div>
                        <div style={styles.modalBody}>
                            <p>Are you sure you want to delete <strong>{selectedJobs.length}</strong> job(s)?</p>
                            <p>This action cannot be undone.</p>
                        </div>
                        <div style={styles.modalFooter}>
                            <button
                                style={{...styles.actionButton, background: '#dc3545', color: 'white', padding: '0.6rem 1.5rem'}}
                                onClick={handleBulkDelete}
                            >
                                Yes, Delete All
                            </button>
                            <button
                                style={{...styles.actionButton, background: '#6c757d', color: 'white', padding: '0.6rem 1.5rem'}}
                                onClick={() => setShowBulkDeleteConfirm(false)}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SuperAdmin;